import React from "react";
import { Building2, Mail, Phone, Image as ImageIcon } from "lucide-react";
import SectionHeader from "../shared/SectionHeader";
import LogoUploadField from "../shared/LogoUploadField";
import { UPLOAD_KEYS } from "../../../contexts/CompanySettingsContext";

/**
 * Company Information Form Section
 * 
 * @param {Object} props Component properties
 * @param {Function} props.register React Hook Form register function
 * @param {Object} props.errors React Hook Form errors object
 * @param {string} props.logoPreview URL or data URL for logo preview
 * @param {Function} props.handleLogoChange Handler for logo file changes
 * @param {Object} props.uploadProgress Upload progress status object
 * @param {boolean} props.isUploading Whether an upload is in progress
 */
const CompanyInfoForm = ({
  register,
  errors,
  logoPreview,
  handleLogoChange,
  uploadProgress,
  isUploading,
}) => (
  <div className="card bg-base-100 border border-base-300">
    <div className="card-body p-6">
      <SectionHeader
        icon={<Building2 className="w-6 h-6 text-primary" aria-hidden="true" />}
        title="Company Information"
        description="Manage your organization details and branding"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Company Name</span>
            </label>
            <input
              type="text"
              placeholder="Enter company name"
              className="input input-bordered w-full"
              {...register("companyName", {
                required: "Company name is required",
              })}
              aria-invalid={errors.companyName ? "true" : "false"}
              aria-describedby={
                errors.companyName ? "company-name-error" : undefined
              }
            />
            {errors.companyName && (
              <p className="text-error text-sm mt-1" id="company-name-error">
                {errors.companyName.message}
              </p>
            )}
          </div>

          <LogoUploadField
            title="Company Logo"
            description="This logo will be used across the platform and as a default for events."
            preview={logoPreview}
            progress={uploadProgress[UPLOAD_KEYS.COMPANY_LOGO] || 0}
            onChange={handleLogoChange}
            icon={<ImageIcon className="w-5 h-5" aria-hidden="true" />}
            type="company"
            isUploading={isUploading}
          />
        </div>

        <div className="space-y-4">
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text flex items-center gap-2">
                <Mail className="w-4 h-4" aria-hidden="true" />
                Contact Email
              </span>
            </label>
            <input
              type="email"
              className="input input-bordered w-full"
              {...register("contactEmail", {
                required: "Email is required",
              })}
              aria-invalid={errors.contactEmail ? "true" : "false"}
              aria-describedby={
                errors.contactEmail ? "contact-email-error" : undefined
              }
            />
            {errors.contactEmail && (
              <p className="text-error text-sm mt-1" id="contact-email-error">
                {errors.contactEmail.message}
              </p>
            )}
          </div>

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text flex items-center gap-2">
                <Phone className="w-4 h-4" aria-hidden="true" />
                Contact Phone
              </span>
            </label>
            <input
              type="tel"
              className="input input-bordered w-full"
              {...register("contactPhone")}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default CompanyInfoForm; 